/**
 * Cross-tenant audit log viewer — super-admin only.
 *
 * Lists state-changing events across EVERY tenant + super-admin-global events
 * (society_id=null). RLS-bypassing reader is on the API side via
 * `withoutTenant('superadmin.audit_list')`.
 *
 * Filters: society, action (partial), actor role, date range. Cursor pagination
 * over (at DESC, id DESC). Row click expands before/after JSON for the row.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  ScrollText,
  Filter,
  Activity,
  Clock3,
  Building,
  Users2,
  X
} from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { formatDateTime } from '@/lib/format';

interface AuditLogEntry {
  id: string;
  society_id: string | null;
  society_name: string | null;
  actor_user_id: string | null;
  actor_role: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  user_agent: string | null;
  at: string;
}

interface ListResponse {
  items: AuditLogEntry[];
  next_cursor: string | null;
}

interface SocietyRow {
  id: string;
  name: string;
}

interface SocietiesResponse {
  items: SocietyRow[];
  next_cursor: string | null;
}

const ACTOR_ROLES = [
  'resident',
  'guard',
  'admin',
  'accountant',
  'treasurer',
  'secretary',
  'president',
  'super_admin'
] as const;

const ROLE_VARIANT: Record<string, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  admin: 'info',
  super_admin: 'alert',
  resident: 'ok',
  guard: 'warn'
};

function ActionPill({ action }: { action: string }): JSX.Element {
  const [entity, verb] = action.includes('.') ? action.split('.') : [action, ''];
  return (
    <span className="inline-flex items-center gap-1 rounded-vt-sm bg-vt-surface-2 px-2 py-0.5 font-mono text-vt-12 text-vt-text">
      <span className="text-vt-text-muted">{entity}</span>
      {verb ? <span className="font-medium text-vt-primary">.{verb}</span> : null}
    </span>
  );
}

function JsonPreview({ data }: { data: Record<string, unknown> | null }): JSX.Element {
  if (!data || Object.keys(data).length === 0) {
    return <span className="text-vt-text-muted">—</span>;
  }
  return (
    <pre className="max-h-[420px] overflow-auto rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-12 text-vt-text">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function todayStartIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function weekStartIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 7);
  return d.toISOString();
}

export function SuperAdminAuditPage(): JSX.Element {
  // ─────────────── Filter state ───────────────
  const [societyId, setSocietyId] = useState<string>('');
  const [actionInput, setActionInput] = useState<string>('');
  const [action, setAction] = useState<string>(''); // debounced
  const [actorRole, setActorRole] = useState<string>('');
  const [fromInput, setFromInput] = useState<string>('');
  const [toInput, setToInput] = useState<string>('');
  const [drillId, setDrillId] = useState<string | null>(null);

  // Cursor pagination state — first page comes from useQuery, additional pages
  // are accumulated into `extraPages` so changing filters doesn't lose context.
  const [extraPages, setExtraPages] = useState<AuditLogEntry[]>([]);
  const [moreCursor, setMoreCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  // Debounce action text — partial match on backend, so 300ms idle suffices.
  useEffect(() => {
    const t = setTimeout(() => setAction(actionInput.trim()), 300);
    return () => clearTimeout(t);
  }, [actionInput]);

  // Fetch the tenant directory once for the dropdown — cap at 100 for V0.1 scale.
  const societies = useQuery({
    queryKey: ['superadmin', 'societies-for-audit'],
    queryFn: () => get<SocietiesResponse>('/superadmin/societies?limit=100&sort=name_asc'),
    staleTime: 60_000
  });

  // First-page query. Cursor is intentionally NOT in the queryKey — pagination
  // adds rows imperatively (see onLoadMore) so the first page can refetch
  // independently when filters change.
  const list = useQuery({
    queryKey: ['superadmin', 'audit-log', { societyId, action, actorRole, fromInput, toInput }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (societyId) params.set('society_id', societyId);
      if (action) params.set('action', action);
      if (actorRole) params.set('actor_role', actorRole);
      if (fromInput) params.set('from', new Date(fromInput).toISOString());
      if (toInput) params.set('to', new Date(toInput).toISOString());
      return get<ListResponse>(`/superadmin/audit-log?${params.toString()}`);
    },
    refetchOnWindowFocus: false
  });

  // KPI strip — "today" + "this week" + "distinct tenants active today".
  // Pull small payload (limit=100) of today's events and compute client-side.
  const todayQuery = useQuery({
    queryKey: ['superadmin', 'audit-log', 'today'],
    queryFn: () =>
      get<ListResponse>(`/superadmin/audit-log?limit=100&from=${encodeURIComponent(todayStartIso())}`),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });
  const weekQuery = useQuery({
    queryKey: ['superadmin', 'audit-log', 'week'],
    queryFn: () =>
      get<ListResponse>(`/superadmin/audit-log?limit=100&from=${encodeURIComponent(weekStartIso())}`),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  // Whenever first-page data lands, reseed pagination state from it.
  useEffect(() => {
    if (list.data) {
      setExtraPages([]);
      setMoreCursor(list.data.next_cursor);
      setHasMore(Boolean(list.data.next_cursor));
    }
  }, [list.data]);

  const firstPage = list.data?.items ?? [];
  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const out: AuditLogEntry[] = [];
    for (const r of [...firstPage, ...extraPages]) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    return out;
  }, [firstPage, extraPages]);

  const drillEntry = useMemo(
    () => (drillId ? allItems.find((e) => e.id === drillId) ?? null : null),
    [drillId, allItems]
  );

  const onLoadMore = async () => {
    if (!moreCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('cursor', moreCursor);
      if (societyId) params.set('society_id', societyId);
      if (action) params.set('action', action);
      if (actorRole) params.set('actor_role', actorRole);
      if (fromInput) params.set('from', new Date(fromInput).toISOString());
      if (toInput) params.set('to', new Date(toInput).toISOString());
      const next = await get<ListResponse>(`/superadmin/audit-log?${params.toString()}`);
      setExtraPages((prev) => {
        const seen = new Set<string>([...firstPage, ...prev].map((r) => r.id));
        return [...prev, ...next.items.filter((r) => !seen.has(r.id))];
      });
      setMoreCursor(next.next_cursor);
      setHasMore(Boolean(next.next_cursor));
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const filtersActive = Boolean(societyId || actionInput || actorRole || fromInput || toInput);

  const clearFilters = (): void => {
    setSocietyId('');
    setActionInput('');
    setAction('');
    setActorRole('');
    setFromInput('');
    setToInput('');
  };

  // KPI: events today / this week / distinct tenants active today.
  // We use the small 100-row sample as a proxy — accurate up to that cap, which
  // is plenty for V0.1 pilot scale.
  const kpiToday = todayQuery.data?.items.length ?? 0;
  const kpiWeek = weekQuery.data?.items.length ?? 0;
  const kpiTenantsToday = useMemo(() => {
    const ids = new Set<string>();
    for (const r of todayQuery.data?.items ?? []) {
      if (r.society_id) ids.add(r.society_id);
    }
    return ids.size;
  }, [todayQuery.data]);

  return (
    <>
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-6">
        <div className="flex items-center justify-between gap-vt-4">
          <div>
            <h1 className="text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
              Cross-tenant audit log
            </h1>
            <p className="mt-1 text-vt-13 text-vt-text-muted">
              Every state-changing event across all tenants, plus super-admin-global events.
            </p>
          </div>
          <Link
            to="/superadmin/dashboard"
            className="text-vt-13 text-vt-primary hover:underline"
          >
            ← Back to tenants
          </Link>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Activity className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Events today</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {todayQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiToday}
                  {kpiToday === 100 ? (
                    <span className="ml-1 text-vt-12 font-normal text-vt-text-muted">+</span>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <ScrollText className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Events this week</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {weekQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiWeek}
                  {kpiWeek === 100 ? (
                    <span className="ml-1 text-vt-12 font-normal text-vt-text-muted">+</span>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <Users2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Tenants active today</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {todayQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiTenantsToday}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter row */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
            <div className="flex items-center gap-vt-2 self-center">
              <Filter className="h-4 w-4 text-vt-text-muted" />
              <span className="text-vt-12 text-vt-text-muted">Filters:</span>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="audit-society">Society</Label>
              <Select
                id="audit-society"
                value={societyId}
                onChange={(e) => setSocietyId(e.target.value)}
                style={{ minWidth: 220 }}
                disabled={societies.isLoading}
              >
                <option value="">All societies</option>
                {societies.data?.items.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="audit-action">Action contains</Label>
              <Input
                id="audit-action"
                type="search"
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                placeholder="notice.publish"
                style={{ minWidth: 200 }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="audit-role">Actor role</Label>
              <Select
                id="audit-role"
                value={actorRole}
                onChange={(e) => setActorRole(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">All roles</option>
                {ACTOR_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="audit-from">From</Label>
              <Input
                id="audit-from"
                type="datetime-local"
                value={fromInput}
                onChange={(e) => setFromInput(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="audit-to">To</Label>
              <Input
                id="audit-to"
                type="datetime-local"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
              />
            </div>

            {filtersActive ? (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="self-end">
                <X className="h-4 w-4" /> Clear filters
              </Button>
            ) : null}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<ScrollText className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load audit log"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : allItems.length === 0 ? (
              <EmptyState
                icon={<ScrollText className="h-6 w-6" strokeWidth={1.75} />}
                title="No audit events"
                description={
                  filtersActive
                    ? 'No events match the current filters.'
                    : 'No state-changing actions have been recorded yet.'
                }
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Society</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allItems.map((e) => (
                      <TableRow
                        key={e.id}
                        onClick={() => setDrillId(e.id)}
                        className="cursor-pointer transition-colors hover:bg-vt-surface-2"
                      >
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          <Clock3 className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5" />
                          {formatDateTime(e.at)}
                        </TableCell>
                        <TableCell>
                          {e.society_id && e.society_name ? (
                            <div className="flex flex-col">
                              <span className="text-vt-text">{e.society_name}</span>
                              <span className="font-mono text-vt-11 text-vt-text-muted">
                                {e.society_id.slice(0, 8)}…
                              </span>
                            </div>
                          ) : (
                            <Badge variant="muted">
                              <Building className="mr-1 h-3 w-3" /> Global
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <ActionPill action={e.action} />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <Badge variant={ROLE_VARIANT[e.actor_role] ?? 'muted'}>{e.actor_role}</Badge>
                            {e.actor_user_id ? (
                              <span className="mt-0.5 font-mono text-vt-11 text-vt-text-muted">
                                {e.actor_user_id.slice(0, 8)}…
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-vt-text">{e.entity_type}</span>
                            {e.entity_id ? (
                              <span className="font-mono text-vt-11 text-vt-text-muted">
                                {e.entity_id.slice(0, 8)}…
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-vt-11 text-vt-text-muted">{e.ip ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <CursorPaginator
                  hasMore={hasMore}
                  loading={loadingMore}
                  itemsShown={allItems.length}
                  pageSize={50}
                  onLoadMore={() => void onLoadMore()}
                />
                {!hasMore && allItems.length > 0 ? (
                  <div className="mt-vt-4 text-center text-vt-12 text-vt-text-muted">
                    End of log — no more entries.
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drill-down dialog */}
      <Dialog open={Boolean(drillId)} onOpenChange={(v) => (v ? null : setDrillId(null))}>
        <DialogContent className="max-w-3xl">
          {drillEntry ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-vt-2">
                  <ActionPill action={drillEntry.action} />
                </DialogTitle>
                <DialogDescription>
                  {formatDateTime(drillEntry.at)} · {drillEntry.actor_role}
                  {drillEntry.society_name ? ` · ${drillEntry.society_name}` : ' · Global'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-vt-4 py-vt-2">
                <div className="grid grid-cols-2 gap-vt-3 text-vt-12">
                  <div>
                    <div className="text-vt-text-muted">Actor user id</div>
                    <div className="font-mono text-vt-text">{drillEntry.actor_user_id ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">IP</div>
                    <div className="font-mono text-vt-text">{drillEntry.ip ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">Entity</div>
                    <div className="text-vt-text">
                      {drillEntry.entity_type}
                      {drillEntry.entity_id ? (
                        <span className="ml-1 font-mono text-vt-11 text-vt-text-muted">
                          {drillEntry.entity_id}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">User agent</div>
                    <div className="truncate text-vt-text" title={drillEntry.user_agent ?? ''}>
                      {drillEntry.user_agent ?? '—'}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-1 text-vt-12 font-semibold uppercase tracking-wider text-vt-text-muted">
                    Before
                  </h4>
                  <JsonPreview data={drillEntry.before} />
                </div>
                <div>
                  <h4 className="mb-1 text-vt-12 font-semibold uppercase tracking-wider text-vt-text-muted">
                    After
                  </h4>
                  <JsonPreview data={drillEntry.after} />
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
